import { TrafficCamera, TrafficCameraResponse } from '../types';

// Backend API endpoint (since we're now using our own backend)
const BACKEND_API_BASE = 'http://localhost:5001/api';

// Live Traffic base URL for external links
const LIVE_TRAFFIC_BASE = 'https://www.livetraffic.com';

/**
 * Fetch traffic cameras from our backend API
 * This now calls our Flask backend instead of the external API
 */
export const fetchTrafficCameras = async (): Promise<TrafficCameraResponse> => {
  try {
    // console.log('🚦 Fetching traffic cameras from backend API...');

    // Call our backend API
    const response = await fetch(`${BACKEND_API_BASE}/traffic/cameras`);

    if (!response.ok) {
      throw new Error(`Backend API request failed: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    // console.log('✅ Backend API response received:', data);

    return data;
  } catch (error) {
    console.error('❌ Error fetching traffic cameras from backend:', error);
    // console.log('🔄 Falling back to mock data...');
    // Fallback to mock data on error
    return getMockTrafficCameras();
  }
};

/**
 * Fetch specific traffic camera by ID from our backend
 */
export const fetchTrafficCameraById = async (cameraId: string): Promise<TrafficCamera | null> => {
  try {
    // console.log(`🚦 Fetching specific camera from backend: ${cameraId}`);

    const response = await fetch(`${BACKEND_API_BASE}/traffic/cameras/${cameraId}`);

    if (!response.ok) {
      throw new Error(`Backend API request failed: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    // console.log(`✅ Camera ${cameraId} data received from backend:`, data);

    return data;
  } catch (error) {
    console.error(`❌ Error fetching camera ${cameraId} from backend:`, error);
    // Fallback to mock data
    const mockData = getMockTrafficCameras();
    return mockData.cameras.find((camera) => camera.id === cameraId) || null;
  }
};

/**
 * Get mock traffic camera data for ANZAC Bridge
 * This simulates the real API response structure
 */
const getMockTrafficCameras = (): TrafficCameraResponse => {
  const now = new Date();

  return {
    cameras: [
      {
        id: 'anzac-bridge-north',
        name: 'ANZAC Bridge - Northbound',
        description: 'Traffic camera view northbound on ANZAC Bridge towards Pyrmont',
        latitude: -33.87,
        longitude: 151.185,
        imageUrl: `${LIVE_TRAFFIC_BASE}/cameras/anzac-bridge-north.jpg`,
        lastUpdated: now.toISOString(),
        status: 'active',
        direction: 'Northbound',
        roadName: 'ANZAC Bridge',
      },
      {
        id: 'anzac-bridge-south',
        name: 'ANZAC Bridge - Southbound',
        description: 'Traffic camera view southbound on ANZAC Bridge towards Glebe',
        latitude: -33.87,
        longitude: 151.185,
        imageUrl: `${LIVE_TRAFFIC_BASE}/cameras/anzac-bridge-south.jpg`,
        lastUpdated: now.toISOString(),
        status: 'active',
        direction: 'Southbound',
        roadName: 'ANZAC Bridge',
      },
      {
        id: 'anzac-bridge-ramp',
        name: 'ANZAC Bridge - Western Distributor Ramp',
        description: 'Traffic camera view of the Western Distributor ramp to ANZAC Bridge',
        latitude: -33.87,
        longitude: 151.185,
        imageUrl: `${LIVE_TRAFFIC_BASE}/cameras/anzac-bridge-ramp.jpg`,
        lastUpdated: now.toISOString(),
        status: 'active',
        direction: 'Ramp',
        roadName: 'Western Distributor',
      },
      {
        id: 'anzac-bridge-west',
        name: 'ANZAC Bridge - Westbound',
        description: 'Traffic camera view westbound on ANZAC Bridge towards Rozelle',
        latitude: -33.87,
        longitude: 151.185,
        imageUrl: `${LIVE_TRAFFIC_BASE}/cameras/anzac-bridge-west.jpg`,
        lastUpdated: now.toISOString(),
        status: 'maintenance',
        direction: 'Westbound',
        roadName: 'ANZAC Bridge',
      },
    ],
    lastUpdated: now.toISOString(),
  };
};

/**
 * Get traffic camera status
 */
export const getCameraStatus = (camera: TrafficCamera): 'active' | 'inactive' | 'maintenance' => {
  // In a real implementation, this might check the camera's health status
  // For now, we'll use the stored status
  return camera.status;
};

/**
 * Check if camera is currently active
 */
export const isCameraActive = (camera: TrafficCamera): boolean => {
  return camera.status === 'active';
};

/**
 * Get camera image URL with fallback
 */
export const getCameraImageUrl = (camera: TrafficCamera): string => {
  if (camera.status === 'active' && camera.imageUrl) {
    return camera.imageUrl;
  }

  // Return a placeholder image URL
  return `${LIVE_TRAFFIC_BASE}/images/camera-offline.jpg`;
};

/**
 * Get traffic information for a specific area
 */
export const getTrafficInfo = async (latitude: number, longitude: number, radius: number = 1000) => {
  try {
    const cameras = await fetchTrafficCameras();

    // Filter cameras within the specified radius
    const nearbyCameras = cameras.cameras.filter((camera) => {
      const distance = calculateDistance(latitude, longitude, camera.latitude, camera.longitude);
      return distance <= radius;
    });

    return {
      cameras: nearbyCameras,
      totalCameras: nearbyCameras.length,
      activeCameras: nearbyCameras.filter((camera) => camera.status === 'active').length,
    };
  } catch (error) {
    console.error('Error getting traffic info:', error);
    return null;
  }
};

/**
 * Fetch ANZAC Bridge specific traffic cameras
 * This function now uses our backend API
 */
export const fetchAnzacBridgeCameras = async (): Promise<TrafficCameraResponse> => {
  try {
    // console.log('🚦 Fetching ANZAC Bridge traffic cameras from backend...');

    // Since our backend already provides ANZAC Bridge cameras, we can just call the main endpoint
    const response = await fetch(`${BACKEND_API_BASE}/traffic/cameras`);

    if (!response.ok) {
      throw new Error(`Backend API request failed: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    // console.log(`✅ Found ${data.cameras.length} cameras from backend`);

    return data;
  } catch (error) {
    console.error('❌ Error fetching ANZAC Bridge cameras from backend:', error);
    // console.log('🔄 Falling back to mock data...');
    return getMockTrafficCameras();
  }
};

/**
 * Get camera image with real-time updates
 * This now uses our backend API
 */
export const getCameraImage = async (cameraId: string): Promise<string | null> => {
  try {
    // console.log(`🖼️ Fetching camera image for ${cameraId} from backend...`);

    // For now, we'll use the mock image URLs since our backend doesn't provide real images yet
    // In the future, we could add an image endpoint to the backend
    return `https://www.livetraffic.com/cameras/${cameraId}.jpg`;
  } catch (error) {
    console.error(`Error getting camera image for ${cameraId}:`, error);
    return `https://www.livetraffic.com/cameras/${cameraId}.jpg`;
  }
};

/**
 * Calculate distance between two coordinates using Haversine formula
 */
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Get peak hour information for ANZAC Bridge
 */
export const getPeakHours = () => {
  return {
    morning: { start: '07:00', end: '09:00', description: 'Morning rush hour' },
    evening: { start: '17:00', end: '19:00', description: 'Evening rush hour' },
    weekend: { start: '10:00', end: '18:00', description: 'Weekend traffic' },
  };
};

/**
 * Get alternative routes for ANZAC Bridge
 */
export const getAlternativeRoutes = () => {
  return [
    {
      name: 'Pyrmont Bridge',
      description: 'Alternative route via Pyrmont Bridge',
      distance: '2.5 km',
      estimatedTime: '8-12 minutes',
    },
    {
      name: 'Glebe Island Bridge',
      description: 'Alternative route via Glebe Island Bridge',
      distance: '3.2 km',
      estimatedTime: '10-15 minutes',
    },
    {
      name: 'Anzac Bridge (westbound)',
      description: 'Use westbound lanes if available',
      distance: '0.5 km',
      estimatedTime: '2-3 minutes',
    },
  ];
};

/**
 * Check API connectivity and key validity
 * This now checks our backend API
 */
export const checkApiStatus = async (): Promise<{
  isConnected: boolean;
  hasValidKey: boolean;
  error?: string;
}> => {
  try {
    // console.log('🔍 Checking backend API status...');

    // Try a simple API call to test connectivity to our backend
    const response = await fetch(`${BACKEND_API_BASE}/traffic/cameras`);

    if (response.ok) {
      return {
        isConnected: true,
        hasValidKey: true,
      };
    }

    return {
      isConnected: true,
      hasValidKey: false,
      error: `Backend API returned status ${response.status}`,
    };
  } catch (error) {
    console.error('❌ Backend API status check failed:', error);
    return {
      isConnected: false,
      hasValidKey: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Get API usage statistics (if available)
 * This now checks our backend API
 */
export const getApiUsage = async (): Promise<{
  requestsRemaining?: number;
  resetTime?: string;
  quota?: string;
}> => {
  try {
    // Check response headers for rate limiting info from our backend
    const response = await fetch(`${BACKEND_API_BASE}/traffic/cameras`);

    const usage = {
      requestsRemaining: response.headers.get('X-RateLimit-Remaining')
        ? parseInt(response.headers.get('X-RateLimit-Remaining')!)
        : undefined,
      resetTime: response.headers.get('X-RateLimit-Reset') || undefined,
      quota: response.headers.get('X-RateLimit-Limit') || undefined,
    };

    if (usage.requestsRemaining !== undefined) {
      // console.log(`📊 Backend API Usage: ${usage.requestsRemaining} requests remaining`);
    }

    return usage;
  } catch (error) {
    console.error('Error getting backend API usage:', error);
    return {};
  }
};

export interface TrafficHazard {
  id: string;
  title: string;
  description: string;
  severity: string;
  status: string;
  latitude: number;
  longitude: number;
  lastUpdated: string;
  roadName?: string;
  direction?: string;
  distance?: number;
}

export const fetchTrafficHazards = async (latitude?: number, longitude?: number): Promise<TrafficHazard[]> => {
  try {
    const url = 'https://api.transport.nsw.gov.au/v1/live/hazards/incident/all';
    
    const headers = {
      accept: 'application/json',
      Authorization:
        'apikey eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJqdGkiOiJtdHItRUFyQVlndGhLTEVJVkYweGptYjd4eXJQaGs1NnBpczhqTnBRZ1NZIiwiaWF0IjoxNzM5NDM5MjI1fQ.RGWbMfYOr6Z7doBlbxj4JOhhgW9ZucEhn1KbRxcxuJc',
    };

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    // console.log('🚨 Hazards API response - Total features:', data.features?.length || 0);

    // Transform the API response to our interface using correct property names
    let hazards: TrafficHazard[] =
      data.features?.map((feature: any) => {
        const props = feature.properties;
        const roads = props.roads || [];
        const primaryRoad = roads[0] || {};
        
        return {
          id: feature.id,
          title: props.displayName || props.headline || 'Traffic Incident',
          description: props.otherAdvice?.replace(/<[^>]*>/g, '') || props.adviceA || 'No description available',
          severity: props.isMajor ? 'High' : props.mainCategory === 'BREAKDOWN' ? 'Medium' : 'Low',
          status: props.ended ? 'Resolved' : props.incidentKind === 'Planned' ? 'Planned' : 'Active',
          latitude: feature.geometry.coordinates[1],
          longitude: feature.geometry.coordinates[0],
          lastUpdated: props.lastUpdated ? new Date(props.lastUpdated).toISOString() : new Date().toISOString(),
          roadName: primaryRoad.mainStreet || props.displayName?.split(' ')[0] || 'Unknown Road',
          direction: primaryRoad.direction || roads[0]?.direction || 'Both directions',
        };
      }) || [];

    // Filter out ended/resolved incidents
    hazards = hazards.filter((hazard) => hazard.status !== 'Resolved');
    
    // If coordinates provided, filter to 2km radius client-side
    if (latitude && longitude) {
      hazards = hazards.filter((hazard) => {
        const distance = calculateDistance(latitude, longitude, hazard.latitude, hazard.longitude);
        return distance <= 2;
      });
      // console.log(`🚨 Filtered to ${hazards.length} active hazards within 2km`);
    }

    // console.log(`🚨 Returning ${hazards.length} traffic hazards`);
    return hazards;
  } catch (error) {
    console.error('❌ Error fetching traffic hazards:', error);
    return [];
  }
};
